/**
 * Shared style tokens for Purchase Contract form dialogs.
 *
 * Inspired by `saleContract/SaleAmendmentFormDialog` — compact 0.8125rem
 * inputs, uppercase section headers, shaded read-only fields. Apply via
 * the DialogContent `sx` prop and per-field `sx` overrides so every
 * dialog under /purchase/contract/* shares the same look.
 */

import type { Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'

/** Apply on `<DialogContent sx={compactDialogContentSx}>` for tight inputs. */
export const compactDialogContentSx: SystemStyleObject<Theme> = {
  pt: 0.75,
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
  '& .MuiFormHelperText-root': { fontSize: '0.7rem', mt: 0.25 },
  '& .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiCheckbox-root': { p: 0.5 },
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
  '& .MuiTypography-body2': { fontSize: '0.8125rem' },
}

/** Compact DialogTitle with room for inline Save/Cancel buttons.
 *  flexWrap nowrap để buttons KHÔNG xuống dòng khi title dài trên mobile —
 *  title tự truncate qua dialogTitleTextSx + noWrap prop trên Typography. */
export const dialogTitleSx: SystemStyleObject<Theme> = {
  pb: 1,
  pt: 1.25,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'nowrap',
}

/** Title Typography sx: truncate dài + giữ flex-grow.
 *  Combo: Typography component="span" noWrap title={titleText}
 *         sx={dialogTitleTextSx}. */
export const dialogTitleTextSx: SystemStyleObject<Theme> = {
  flex: '1 1 auto',
  fontWeight: 700,
  fontSize: '1rem',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

/** Save/Cancel button Stack — flexShrink để KHÔNG bị squeeze khi title dài. */
export const dialogTitleActionsSx: SystemStyleObject<Theme> = {
  flexShrink: 0,
  ml: 'auto',
}

/** Save/Cancel button size in DialogTitle — đồng nhất với Sales Contract canonical. */
export const dialogHeaderBtnSx: SystemStyleObject<Theme> = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none',
}

/** Uppercase section header caption used above each `<Divider/>`. */
export const sectionHeaderSx: SystemStyleObject<Theme> = {
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  fontSize: '0.68rem',
}

/** Read-only TextField shading — neutral gray bg, secondary text, bold.
 *  Skill `/convert-form`: light `#f1f3f5`, dark `rgba(255,255,255,0.06)`.
 *  Trước đây dùng `action.hover` (4% black overlay) → trên theme TLC cream
 *  (#fbf6ef) trông phớt hồng/cam, user khó phân biệt với editable. Neutral
 *  gray cố định để readonly luôn rõ ràng bất kể theme background. */
export const roSx: SystemStyleObject<Theme> = {
  '& .MuiInputBase-root': {
    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5'),
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
}

/** Right-align numeric inputs. */
export const numSx: SystemStyleObject<Theme> = {
  '& input': { textAlign: 'right' },
}

/** Compact info `<Alert/>` used for instruction panels. */
export const instructionAlertSx: SystemStyleObject<Theme> = {
  mt: 2,
  py: 0.5,
  whiteSpace: 'pre-line',
  fontSize: '0.75rem',
  '& .MuiAlert-message': { py: 0.25 },
}
